import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Image, Modal, ModalProps, Platform, StyleSheet, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import { BgLandscapePng, BgPortraitPng } from '../../../assets';
import Portal from '../../../shared/Portal';
import { useDevice } from '../../../utils/hooks/useDevice';
import { useSafeViewport } from '../../../utils/hooks/useSafeViewport';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { KeyboardLift } from '../../../utils/keyboard/KeyboardAvoidance';
import {
	achievementsColors,
	achievementsGlow,
	RADIUS_PANEL,
} from '../Achievements/achievementsTheme';
import { GradientFill } from '../common/GradientFill';
import { Pressable } from '../common/Touchable';
import DesktopPanelFill from '../Desktop/DesktopPanelFill';
// The profile is one of three tabs and its artboard is the one the panel follows, so the geometry
// lives with the other two rather than being repeated here.
import { PANEL_BOTTOM_GAP, PANEL_HEIGHT, PANEL_WIDTH, PANEL_Y } from '../Inventory/inventoryTheme';
import { useRedesignScale } from './useRedesignScale';

/**
 * The chrome the profile modal shares with the achievements and inventory modals - Figma
 * "Profile / Modal — Full content no vip" (4022:37706) on page "BOGI DRAFT", which draws the same
 * panel those two use: a warm scrim over the app, then a 358-wide panel at 16/44 with a gold
 * hairline, a 16 radius and the orange bloom.
 *
 * Like them it paints the app's backdrop inside the panel: on the artboard the panel sits over a
 * static "Backdrop / Home / Mobile" and carries no fill of its own, so whatever screen is behind
 * must not read through it.
 *
 * The design's panel is 853 tall on a 957 frame; on a shorter viewport it takes what is left
 * rather than overflowing, and its body scrolls either way.
 *
 * A shorter popup (e.g. "Modal / Settings", 5054:78827) passes its own `panel` geometry: the same
 * 358-wide panel, but its own height and radius, centred on the screen rather than pinned at 44.
 */
export type PanelGeometry = {
	/** Design-space height of the panel. */
	height: number;
	/** Design-space width; defaults to the shared 358 (wider only for a desktop-only layout). */
	width?: number;
	/** Design-space top position. When omitted, the panel keeps its existing centred/default position. */
	top?: number;
	/** Design-space corner radius; defaults to the tabbed modals' 16. */
	radius?: number;
	/** A tint laid over the panel's backdrop, for a popup drawn darker than the screen. */
	tint?: string;
	/**
	 * Float over the current screen instead of repainting the app backdrop behind the panel: the
	 * screen stays visible under the scrim.
	 */
	overScreen?: boolean;
	/** Overrides the shared overlay tint for a modal that needs a different backdrop contrast. */
	scrimColor?: string;
	/** The panel's rim; defaults to the tabbed modals' bright gold. */
	borderColor?: string;
	/** Design-space rim width; defaults to 1. */
	borderWidth?: number;
	/**
	 * Paint the panel with its own top-to-bottom gradient ([offset, colour] stops) instead of a
	 * copy of the app backdrop - for a sheet the design fills, like the tournament modals.
	 */
	fill?: Array<[string, string]>;
	/** A top-to-bottom gradient rim ([offset, colour] stops) in place of `borderColor`. */
	rim?: Array<[string, string]>;
};

/**
 * Where the panel lands on screen, in pixels. Exported so a modal that draws its own panel - the
 * inventory modal, whose Profile tab is drawn in this one - can sit in exactly the same place.
 */
export const getRedesignPanelFrame = ({
	panel,
	scale,
	width,
	height,
	insetTop,
	isDesktop,
	twin,
}: {
	panel?: PanelGeometry;
	scale: number;
	width: number;
	height: number;
	insetTop: number;
	isDesktop: boolean;
	twin: boolean;
}) => {
	// PANEL_Y is measured against the design's own frame, which already allows for a status bar -
	// but a phone's actual notch/Dynamic Island can sit lower than that, so the panel has to clear
	// whichever is taller instead of starting under it.
	// The desktop frame leaves the same gap above the panel as under it; the phone frame hangs it
	// where the artboard does, clear of the notch.
	const topInset = isDesktop ? PANEL_BOTTOM_GAP * scale : Math.max(PANEL_Y * scale, insetTop);
	const requestedPanelTop =
		panel?.top === undefined ? undefined : Math.max(panel.top * scale, insetTop);
	const panelHeight = panel
		? Math.min(
				panel.height * scale,
				height - (requestedPanelTop ?? insetTop) - PANEL_BOTTOM_GAP * scale,
			)
		: Math.min(PANEL_HEIGHT * scale, height - topInset - PANEL_BOTTOM_GAP * scale);
	const panelTop = panel
		? (requestedPanelTop ?? insetTop + (height - insetTop - panelHeight) / 2)
		: twin
			? (height - panelHeight) / 2
			: topInset;
	const panelWidth = (panel?.width ?? PANEL_WIDTH) * scale;

	return {
		left: (width - panelWidth) / 2,
		top: panelTop,
		width: panelWidth,
		height: panelHeight,
	};
};

/** A 1-wide rounded outline stroked with a vertical gradient, laid over the panel's edge. */
const GradientRim = ({
	stops,
	width,
	height,
	radius,
	strokeWidth,
}: {
	stops: Array<[string, string]>;
	width: number;
	height: number;
	radius: number;
	strokeWidth: number;
}) => {
	const id = useSvgId('panelRim');
	const inset = strokeWidth / 2;

	return (
		<Svg pointerEvents='none' style={StyleSheet.absoluteFill} width={width} height={height}>
			<Defs>
				<LinearGradient id={id} x1='0' y1='0' x2='0' y2='1'>
					{stops.map(([offset, color]) => (
						<Stop key={offset} offset={offset} stopColor={color} />
					))}
				</LinearGradient>
			</Defs>
			<Rect
				x={inset}
				y={inset}
				width={width - strokeWidth}
				height={height - strokeWidth}
				rx={radius - inset}
				ry={radius - inset}
				fill='none'
				stroke={`url(#${id})`}
				strokeWidth={strokeWidth}
			/>
		</Svg>
	);
};

type Props = {
	children: React.ReactNode;
	onClose: () => void;
	renderInline?: boolean;
	panel?: PanelGeometry;
	/**
	 * The scale the modal's content is drawn at, so the panel and what is in it agree. Content
	 * computes it with `useRedesignScale`; left out, the panel works it out the same way.
	 */
	scale?: number;
	/**
	 * On desktop, draw the panel the way the desktop artboards do ("to desktop" page, e.g.
	 * "Edit Profile / Desktop — Screen States" 5605:102663): floating over the blurred live screen
	 * under the scrim, centred, with its own red vignette grain fill instead of a copy of the app
	 * backdrop. No effect on a phone.
	 */
	desktopTwin?: boolean;
	/**
	 * Drawn over the panel's frame but outside its clip, for chrome that overhangs the edge - a
	 * close button sitting on the corner. Positioned against the panel's box.
	 */
	panelOverlay?: React.ReactNode;
	/**
	 * The panel's submit button. When a field in the panel has focus the panel rises above the
	 * keyboard far enough to show the field, and this button too when both fit.
	 */
	keyboardSubmitRef?: React.RefObject<View | null>;
	supportedOrientations?: ModalProps['supportedOrientations'];
};

export const RedesignModal = ({
	children,
	onClose,
	renderInline = false,
	panel,
	scale: contentScale,
	desktopTwin = false,
	panelOverlay,
	keyboardSubmitRef,
	supportedOrientations,
}: Props) => {
	const { t } = useTranslation();
	const { width, height, insets } = useSafeViewport();
	const { isDesktop } = useDevice();

	const ownScale = useRedesignScale();
	const scale = contentScale ?? ownScale;
	const backdrop = isDesktop ? BgLandscapePng : BgPortraitPng;
	const twin = isDesktop && desktopTwin;
	const overScreen = panel?.overScreen || twin;
	const radius = (panel?.radius ?? RADIUS_PANEL) * scale;
	const {
		left: panelLeft,
		top: panelTop,
		width: panelWidth,
		height: panelHeight,
	} = getRedesignPanelFrame({
		panel,
		scale,
		width,
		height,
		insetTop: insets.top,
		isDesktop,
		twin,
	});
	const rimWidth = Math.max(1, (panel?.borderWidth ?? 1) * scale);
	// The panel rises over the keyboard to keep a focused field in sight; the copy of the backdrop
	// it carries moves the other way, so it stays lined up with the screen behind.
	const lift = useRef(new Animated.Value(0)).current;
	const backdropFollow = useRef(Animated.multiply(lift, -1)).current;

	const content = (
		<View style={[styles.overlay, renderInline && styles.inlineOverlay]}>
			{!overScreen && (
				<Image
					source={backdrop}
					style={[styles.screenBackdrop, { width, height }]}
					resizeMode='cover'
				/>
			)}
			<View
				pointerEvents='none'
				style={[styles.scrim, panel?.scrimColor && { backgroundColor: panel.scrimColor }]}
			/>

			<Pressable
				accessibilityRole='button'
				accessibilityLabel={t('T_SITE_A11Y_CLOSE_MODAL', { defaultValue: 'Close modal' })}
				onPress={onClose}
				style={StyleSheet.absoluteFill}
			/>

			<KeyboardLift
				translateY={lift}
				submitRef={keyboardSubmitRef}
				style={[
					styles.panelFrame,
					{ left: panelLeft, top: panelTop, width: panelWidth, height: panelHeight },
				]}
				contentStyle={StyleSheet.absoluteFill}
			>
				<View
					style={[
						styles.panel,
						{
							borderRadius: radius,
							// A gradient rim is drawn over the edge instead, so the view's own border stays out.
							borderWidth: panel?.rim ? 0 : rimWidth,
							...(panel?.borderColor ? { borderColor: panel.borderColor } : null),
						},
					]}
				>
					{/* Its own rounded clip: the panel's `overflow: hidden` does not reliably clip an
					    oversized child to the curve once the panel also carries a shadow. */}
					{twin ? (
						<DesktopPanelFill radius={radius} scale={scale} />
					) : panel?.fill ? (
						<GradientFill stops={panel.fill} radius={radius} />
					) : (
						<View pointerEvents='none' style={[styles.backdropClip, { borderRadius: radius }]}>
							<Animated.Image
								source={backdrop}
								style={[
									styles.backdrop,
									{
										width,
										height,
										left: -panelLeft,
										top: -panelTop,
										transform: [{ translateY: backdropFollow }],
									},
								]}
								resizeMode='cover'
							/>
							{panel?.tint && (
								<View style={[StyleSheet.absoluteFill, { backgroundColor: panel.tint }]} />
							)}
						</View>
					)}

					{children}

					{panel?.rim && !twin && (
						<GradientRim
							stops={panel.rim}
							width={panelWidth}
							height={panelHeight}
							radius={radius}
							strokeWidth={rimWidth}
						/>
					)}
				</View>

				{panelOverlay ? (
					<View pointerEvents='box-none' style={StyleSheet.absoluteFill}>
						{panelOverlay}
					</View>
				) : null}
			</KeyboardLift>
		</View>
	);

	if (renderInline) return content;
	if (Platform.OS === 'web') return <Portal>{content}</Portal>;

	return (
		<Modal
			transparent
			visible
			animationType='fade'
			onRequestClose={onClose}
			statusBarTranslucent
			supportedOrientations={supportedOrientations}
		>
			{content}
		</Modal>
	);
};

const styles = StyleSheet.create({
	overlay: {
		...StyleSheet.absoluteFill,
		zIndex: 10000,
		elevation: 10000,
	},
	inlineOverlay: {
		position: 'absolute',
	},
	screenBackdrop: {
		position: 'absolute',
		left: 0,
		top: 0,
	},
	scrim: {
		...StyleSheet.absoluteFillObject,
		backgroundColor: achievementsColors.scrim,
	},
	panelFrame: {
		position: 'absolute',
	},
	panel: {
		...StyleSheet.absoluteFillObject,
		borderColor: achievementsColors.panelBorder,
		overflow: 'hidden',
		...achievementsGlow.panel,
	},
	backdropClip: {
		...StyleSheet.absoluteFillObject,
		overflow: 'hidden',
	},
	backdrop: {
		position: 'absolute',
	},
});

export default RedesignModal;
